"use client";
import { useEffect, useState, Suspense } from "react";
import EventCard from "@/components/shared/EventCard";
import { Stagger, StaggerItem } from "@/components/motion";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/fetcher";
import { Event } from "@/lib/types/event";
import { Search } from "lucide-react";

export default function ClientEvents() {
  const [events, setEvents] = useState<Event[]>([]);
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get("/events/all", {
        params: {
          search,
          location,
        },
      })
      .then((res) => {
        setEvents(res.data?.data || []);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [search, location]);

  const filteredEvents = events.filter((event) => {
    return (
      event.title.toLowerCase().includes(search.toLowerCase()) &&
      (!location || event.location.toLowerCase().includes(location.toLowerCase()))
    );
  });

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Healthcare Events
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Discover upcoming healthcare events, conferences, workshops, and training sessions in Rwanda.
        </p>
      </div>

      <div className="mb-8 flex sm:justify-end">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-1/2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              placeholder="Search events..."
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              name="search"
            />
          </div>
          <div className="relative w-full sm:w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              placeholder="Filter by location..."
              className="pl-10"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              name="location"
            />
          </div>
        </div>
      </div>

      <Suspense fallback={<div>Loading events...</div>}>
        <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event, index) => (
            <StaggerItem key={event.id} className="h-full">
              <EventCard event={event} index={index} />
            </StaggerItem>
          ))}
        </Stagger>
      </Suspense>

      {!loading && filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600">
            No events found matching your criteria.
          </p>
        </div>
      )}
      {loading && (
        <div className="text-center py-12">
          <p className="text-gray-600">Loading events...</p>
        </div>
      )}
    </div>
  );
} 